// Rezygnacja firmy (FAZA F, F.25) w panelu super-admina: stan z wiersza listy organizacji i zakres
// dni, które okno „Rezygnacja” pozwala wybrać. Reguły pilnuje serwer
// (Login/backend/lib/rezygnacjaKonta.js), tu jest tylko ich lustro dla wyglądu okna: dzień od dziś
// do dziś + 365 w strefie firmy, a dzień w przyszłości tylko dla firmy, która dziś działa; przy
// przerwie zaplanowanej najpóźniej w dniu jej początku (rezygnacja ją zastępuje).
// Czyste funkcje bez Reacta, sprawdzane testem jednostkowym.

import { STREFA_FIRMY } from './format';

export const MAKS_DNI_DO_REZYGNACJI = 365;

/** Dzisiejszy dzień 'RRRR-MM-DD' w strefie firmy (Europe/Warsaw), niezależnie od strefy przeglądarki. */
export function dzisWStrefieFirmy(teraz = new Date()) {
  const czesci = new Intl.DateTimeFormat('en-GB', {
    timeZone: STREFA_FIRMY, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(teraz);
  const wez = (typ) => (czesci.find((c) => c.type === typ) || {}).value;
  return `${wez('year')}-${wez('month')}-${wez('day')}`;
}

/** Dzień 'RRRR-MM-DD' przesunięty o `dni` (liczone na dniach kalendarza, bez zmiany czasu). */
export function dodajDni(ymd, dni) {
  const [r, m, d] = String(ymd).split('-').map(Number);
  const data = new Date(Date.UTC(r, m - 1, d + dni));
  return data.toISOString().slice(0, 10);
}

const poczatekWPrzyszlosci = (wartosc, teraz) => {
  const od = wartosc ? new Date(wartosc) : null;
  return !!od && !Number.isNaN(od.getTime()) && od.getTime() > teraz.getTime();
};

/** 'brak' | 'zaplanowana' | 'trwa' z wiersza listy organizacji (status i resigned_at). */
export function stanRezygnacjiFirmy(org, teraz = new Date()) {
  if (!org || org.status !== 'resigned') return 'brak';
  return poczatekWPrzyszlosci(org.resigned_at, teraz) ? 'zaplanowana' : 'trwa';
}

/**
 * Czy firma dziś działa: aktywna albo z przerwą lub rezygnacją, która się jeszcze nie zaczęła
 * (lustro lib/orgAccess.js#firmaDziala bez sprawdzania terminu demo, bo demo nie ma rezygnacji).
 * Tylko taka firma może dostać rezygnację od dnia w przyszłości.
 */
export function firmaDzisDziala(org, teraz = new Date()) {
  if (!org) return false;
  if (org.status === 'active') return true;
  if (org.status === 'paused') return poczatekWPrzyszlosci(org.paused_at, teraz);
  if (org.status === 'resigned') return poczatekWPrzyszlosci(org.resigned_at, teraz);
  return false;
}

/**
 * Dzień 'RRRR-MM-DD' (strefa firmy) początku przerwy, która jeszcze się nie zaczęła, albo null.
 * Rezygnacja odwołuje taką przerwę, więc nie może zacząć się po tym dniu (serwer: 409
 * `resignation_after_planned_pause`).
 */
export function dzienPrzerwyZaplanowanej(org, teraz = new Date()) {
  if (!org || org.status !== 'paused' || !poczatekWPrzyszlosci(org.paused_at, teraz)) return null;
  return dzisWStrefieFirmy(new Date(org.paused_at));
}

/** Zakres pola z dniem rezygnacji: `{ min, max, tylkoDzis }`. */
export function zakresDniRezygnacji(org, teraz = new Date()) {
  const min = dzisWStrefieFirmy(teraz);
  const przerwa = dzienPrzerwyZaplanowanej(org, teraz);
  const max = dodajDni(min, MAKS_DNI_DO_REZYGNACJI);
  return { min, max: przerwa && przerwa < max ? przerwa : max, tylkoDzis: !firmaDzisDziala(org, teraz) };
}

/** Czy dzień mieści się w zakresie (pusty i niepoprawny nie). */
export function dzienWZakresie(dzien, zakres) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dzien || ''))) return false;
  if (zakres.tylkoDzis) return dzien === zakres.min;
  return dzien >= zakres.min && dzien <= zakres.max;
}

// ── Ręczne usunięcie danych po rezygnacji ───────────────────────────────────────────────────
// Lustro reguł serwera (Login/backend/lib/rezygnacjaKonta.js#usunDanePoRezygnacji), tylko dla
// wyglądu przycisku „Usuń dane firmy” i okna potwierdzenia. Rozstrzyga serwer.

// Najdłuższa nazwa do potwierdzenia (jak na serwerze): dłuższy tekst odpada bez porównywania.
export const MAKS_DLUGOSC_POTWIERDZENIA = 300;

/**
 * Przycisk „Usuń dane firmy” na liście organizacji: `{ widoczne, dostepne, od }`. Widoczny przy
 * firmie po rezygnacji (także zaplanowanej, wtedy wyłączony), nigdy przy demo. Dostępny od dnia
 * usunięcia (`resignation_delete_from` z listy: dzień rezygnacji + 30 w strefie firmy albo
 * późniejszy termin podany firmie w uprzedzeniu mailem, ta sama reguła co serwer), gdy rezygnacja
 * już trwa. `od` to ten dzień 'RRRR-MM-DD' albo null.
 */
export function usuniecieDanychDostepne(org, teraz = new Date()) {
  const widoczne = !!org && !org.is_demo && org.status === 'resigned';
  const od = widoczne && /^\d{4}-\d{2}-\d{2}$/.test(String(org.resignation_delete_from || ''))
    ? org.resignation_delete_from
    : null;
  const dostepne = widoczne && !!od
    && stanRezygnacjiFirmy(org, teraz) === 'trwa'
    && dzisWStrefieFirmy(teraz) >= od;
  return { widoczne, dostepne, od };
}

/**
 * Czy wpisana nazwa potwierdza usunięcie: tekst najwyżej 300 znaków, po normalizacji NFC, zwinięciu
 * każdego ciągu białych znaków (także twardej spacji) do jednej spacji i obcięciu spacji z obu stron
 * równy nazwie firmy, niepusty. Wielkość liter ma znaczenie (jak na serwerze). Okno pokazuje nazwę
 * w HTML, który zwija podwójne spacje, więc bez zwijania takiej nazwy nie dało się potwierdzić.
 */
export function nazwaPotwierdzona(wpisana, nazwa) {
  if (typeof wpisana !== 'string' || wpisana.length > MAKS_DLUGOSC_POTWIERDZENIA) return false;
  const n = (v) => String(v ?? '').normalize('NFC').replace(/\s+/g, ' ').trim();
  return n(wpisana) !== '' && n(wpisana) === n(nazwa);
}
